// /abacus — the abacus on the page: geometry from the box it is given, beads that move
// as a real frame lets them, tap / drag / keyboard, the hint of the next move, the veil
// of a flash card. No knowledge of modes: app.js drives it. Rationale — docs/why/abacus.md.
//
// One ROD element per rod (the schoty: per wire) is the accessible control — role=slider,
// value 0…9, roving tabindex; beads are presentational spans. A hundred focusable beads
// would be a hundred tab stops.

import { KINDS, rodValue, canon, scaledOf, digitsOf, rodsOf } from './core.js';

const EASE = 'cubic-bezier(.25,1.35,.45,1)';

export function createBoard(host, opts = {}) {
    const L = opts.labels || {};
    const st = {
        kind: 'soroban', n: 13, dec: 0, kopecks: false,
        rods: [], g: null, locked: false, digitsOn: true, hints: [],
        focusIdx: -1, lastSound: 0,
    };
    host.classList.add('ab-board');
    host.setAttribute('role', 'group');
    host.setAttribute('dir', 'ltr');
    const frame = el('div', 'ab-fr');
    const inner = el('div', 'ab-in');
    const beam = el('div', 'ab-beam');
    const digits = el('div', 'ab-digs');
    const veil = el('div', 'ab-veil');
    veil.hidden = true;
    frame.appendChild(inner);
    host.append(frame, digits, veil);
    let rodEls = [];

    function el(tag, cls) { const e = document.createElement(tag); if (cls) e.className = cls; return e; }

    /* ─────────── building ─────────── */
    function build() {
        inner.textContent = '';
        digits.textContent = '';
        rodEls = [];
        host.dataset.kind = st.kind;
        const K = KINDS[st.kind];
        if (K.five) inner.appendChild(beam);
        for (let i = 0; i < st.n; i++) {
            const r = el('div', 'ab-rod');
            r.dataset.i = String(i);
            r.setAttribute('role', 'slider');
            r.setAttribute('aria-valuemin', '0');
            r.setAttribute('aria-valuemax', '9');
            r.tabIndex = -1;
            r.appendChild(el('span', 'ab-wire'));
            for (let j = 0; j < K.up; j++) r.appendChild(bead('u', j));
            for (let j = 0; j < K.low; j++) r.appendChild(bead('l', j, !K.five && (j === 4 || j === 5)));
            const place = st.n - 1 - st.dec - i;
            r.style.setProperty('--ab-h', String((place * 47 + 200) % 360));
            if (K.five && isDot(i)) r.classList.add('dot');
            inner.appendChild(r);
            rodEls.push(r);
            const dg = el('span', 'ab-dig');
            digits.appendChild(dg);
        }
        if (!K.five && st.kopecks) {
            // the 4-bead wire between the rubles and the kopecks: the polushka (¼ kopeck);
            // kept as on the real frame, but it does not count — the readout is in kopecks
            const q = el('div', 'ab-rod ab-quarter');
            q.setAttribute('aria-hidden', 'true');
            q.title = L.quarter || '';
            q.appendChild(el('span', 'ab-wire'));
            for (let j = 0; j < 4; j++) q.appendChild(bead('l', j));
            inner.insertBefore(q, rodEls[st.n - st.dec]);
        }
        focusable(st.focusIdx >= 0 && st.focusIdx < st.n ? st.focusIdx : st.n - 1 - st.dec);
        layout();
        paint(false);
    }
    function bead(deck, j, dark) {
        const b = el('span', 'ab-bead' + (deck === 'u' ? ' up' : '') + (dark ? ' dk' : ''));
        b.dataset.deck = deck;
        b.dataset.j = String(j);
        b.appendChild(el('i'));
        return b;
    }
    function isDot(i) {
        const unit = st.n - 1 - st.dec;
        return (unit - i) % 3 === 0;
    }

    /* ─────────── geometry ─────────── */
    // soroban / suanpan, in bead heights (bh): side frame .62, top/bottom .55, a deck =
    // its beads + one bead of travel, the beam .42; a rod pitch is 1.16 bead widths and a
    // bead is 1.72 bead heights wide. Schoty, in bead widths (bw, along the wire): a bead is
    // 1.65 bw tall, a wire is 10 beads + 4 of travel, rows 1.12 bead heights apart.
    function geometryFor(W, H) {
        const K = KINDS[st.kind];
        const nRows = st.n + (!K.five && st.kopecks ? 1 : 0);
        const showDigits = st.digitsOn;
        if (K.five) {
            const ratio = 1.72, pitchK = 1.16;
            const unitsW = 2 * 0.62 + st.n * pitchK * ratio;
            const unitsH = 2 * 0.55 + (K.up + 1) + 0.42 + (K.low + 1) + 0.25 + (showDigits ? 1.15 : 0);
            let bh = Math.min(W / unitsW, H / unitsH, 64);
            bh = Math.max(7, Math.floor(bh * 4) / 4);
            const bw = bh * ratio, P = bw * pitchK;
            const fs = bh * 0.62, ft = bh * 0.55;
            const upH = (K.up + 1) * bh + bh * 0.12, lowH = (K.low + 1) * bh + bh * 0.13, beamH = bh * 0.42;
            const w = Math.round(2 * fs + st.n * P), h = Math.round(2 * ft + upH + beamH + lowH);
            return { five: true, bh, bw, P, fs, ft, upH, lowH, beamH, w, h, digH: showDigits ? Math.round(bh * 1.15) : 0 };
        }
        const travel = 4, beads = 10;
        const tallK = 1.65, pitchK = 1.12;
        const digW = showDigits ? 2.1 : 0;
        const unitsW = 2 * 0.9 + (beads + travel) + digW;
        const unitsH = 2 * 0.45 * tallK + nRows * tallK * pitchK;
        let bw = Math.min(W / unitsW, H / unitsH, 60);
        bw = Math.max(6, Math.floor(bw * 4) / 4);
        const bh = bw * tallK, rowP = bh * pitchK;
        const fs = bw * 0.9, ft = bh * 0.45;
        const len = (beads + travel) * bw;
        const w = Math.round(2 * fs + len), h = Math.round(2 * ft + nRows * rowP);
        return { five: false, bw, bh, rowP, fs, ft, len, w, h, digW: Math.round(digW * bw) };
    }

    function layout() {
        const g = st.g;
        if (!g) return;
        const K = KINDS[st.kind];
        host.style.setProperty('--ab-bw', g.bw + 'px');
        host.style.setProperty('--ab-bh', g.bh + 'px');
        host.style.width = (g.w + (g.five ? 0 : g.digW)) + 'px';
        host.style.height = (g.h + (g.five ? g.digH : 0)) + 'px';
        frame.style.width = g.w + 'px';
        frame.style.height = g.h + 'px';
        inner.style.inset = `${g.ft}px ${g.fs}px`;
        if (g.five) {
            beam.style.top = g.upH + 'px';
            beam.style.height = g.beamH + 'px';
            host.style.setProperty('--ab-beam-y', (g.upH + g.beamH / 2) + 'px');
            rodEls.forEach((r, i) => {
                r.style.left = (i * g.P) + 'px';
                r.style.width = g.P + 'px';
                r.style.top = '0'; r.style.bottom = '0'; r.style.height = '';
            });
            digits.style.top = g.h + 'px';
            digits.style.left = g.fs + 'px';
            digits.style.width = (g.w - 2 * g.fs) + 'px';
            digits.style.height = g.digH + 'px';
            digits.style.gridTemplateColumns = `repeat(${st.n}, ${g.P}px)`;
            digits.style.gridTemplateRows = '';
            digits.style.fontSize = Math.max(12, Math.round(g.bh * 0.78)) + 'px';
        } else {
            const rows = [...inner.querySelectorAll('.ab-rod')];
            rows.forEach((r, k) => {
                r.style.top = (k * g.rowP) + 'px';
                r.style.height = g.rowP + 'px';
                r.style.left = '0'; r.style.width = g.len + 'px';
            });
            digits.style.top = g.ft + 'px';
            digits.style.left = g.w + 'px';
            digits.style.width = g.digW + 'px';
            digits.style.height = (g.h - 2 * g.ft) + 'px';
            digits.style.gridTemplateColumns = '';
            digits.style.gridTemplateRows = `repeat(${rows.length}, ${g.rowP}px)`;
            digits.style.fontSize = Math.max(12, Math.round(g.bh * 0.5)) + 'px';
            // the quarter wire has no digit: an empty cell keeps the rows in line
            const q = inner.querySelector('.ab-quarter');
            digits.querySelectorAll('.gap').forEach((c) => c.remove());
            if (q) {
                const qi = rows.indexOf(q);
                const gap = el('span', 'ab-dig gap');
                digits.insertBefore(gap, digits.children[qi] || null);
            }
        }
        digits.hidden = !st.digitsOn;
    }

    /* ─────────── painting the beads where the rods say ─────────── */
    function beadPos(g, deck, j, count, total) {
        if (g.five) {
            if (deck === 'u') {
                // j = 0 is the bead nearest the beam
                const atBeam = j < count;
                const y = atBeam ? g.upH - (j + 1) * g.bh : (total - 1 - j) * g.bh;
                return [0, y];
            }
            const atBeam = j < count;
            const y = atBeam ? g.upH + g.beamH + j * g.bh : g.upH + g.beamH + g.lowH - (total - j) * g.bh;
            return [0, y];
        }
        // schoty: j = 0 is the LEFTMOST bead; counted beads sit at the left end
        const counted = j < count;
        const x = counted ? j * g.bw : g.len - (total - j) * g.bw;
        return [x, 0];
    }

    function paint(animate = true, slow = false) {
        const g = st.g;
        if (!g) return;
        const K = KINDS[st.kind];
        host.style.setProperty('--ab-t', animate ? (slow ? '.42s' : '.16s') : '0s');
        rodEls.forEach((r, i) => {
            const rod = st.rods[i];
            r.querySelectorAll('.ab-bead').forEach((b) => {
                const deck = b.dataset.deck, j = +b.dataset.j;
                const count = deck === 'u' ? rod.u : rod.l;
                const total = deck === 'u' ? K.up : K.low;
                const [x, y] = beadPos(g, deck, j, count, total);
                b.style.transform = `translate3d(${x.toFixed(2)}px,${y.toFixed(2)}px,0)`;
                b.classList.toggle('on', j < count);
            });
            const v = rodValue(st.kind, rod);
            r.setAttribute('aria-valuenow', String(v));
            r.setAttribute('aria-valuetext', `${placeName(i)}: ${v}`);
            r.setAttribute('aria-label', placeName(i));
            const dg = digitCell(i);
            if (dg) {
                dg.textContent = String(v);
                dg.classList.toggle('z', v === 0);
                dg.classList.toggle('over', v > 9);
                dg.classList.toggle('dp', st.dec > 0 && i === st.n - 1 - st.dec);
            }
        });
        const q = inner.querySelector('.ab-quarter');
        if (q) {
            q.querySelectorAll('.ab-bead').forEach((b) => {
                const [x, y] = beadPos(g, 'l', +b.dataset.j, 0, 4);
                b.style.transform = `translate3d(${x}px,${y}px,0)`;
            });
        }
    }
    function digitCell(i) {
        const cells = [...digits.children].filter((c) => !c.classList.contains('gap'));
        return cells[i] || null;
    }
    function placeName(i) {
        const place = st.n - 1 - st.dec - i;
        if (place < 0) {
            const list = st.kopecks ? L.placesKop : L.placesFrac;
            return (list && list[-place - 1]) || `10^${place}`;
        }
        return (L.places && L.places[place]) || `10^${place}`;
    }

    /* ─────────── changes ─────────── */
    function emit(src, extra) {
        if (opts.onChange) opts.onChange(Object.assign({ src, scaled: scaledOf(st.kind, st.rods), rods: st.rods }, extra || {}));
    }
    function click(n) {
        const now = performance.now();
        if (now - st.lastSound < 25) return;
        st.lastSound = now;
        if (opts.onClack) opts.onClack(n);
    }
    function setRodCount(i, deck, count, src) {
        const K = KINDS[st.kind];
        const rod = st.rods[i];
        const max = deck === 'u' ? K.up : K.low;
        count = Math.max(0, Math.min(max, count));
        const prev = deck === 'u' ? rod.u : rod.l;
        if (count === prev) return false;
        if (deck === 'u') rod.u = count; else rod.l = count;
        clearHints();
        paint(true);
        click(Math.abs(count - prev));
        emit(src || 'user', { rod: i });
        return true;
    }
    function setRodDigit(i, d, src) {
        const K = KINDS[st.kind];
        d = Math.max(0, Math.min(K.five ? 9 : 10, d));
        const c = canon(st.kind, d);
        const rod = st.rods[i];
        if (rod.u === c.u && rod.l === c.l) return;
        const moved = Math.abs(rod.u - c.u) + Math.abs(rod.l - c.l);
        rod.u = c.u; rod.l = c.l;
        clearHints();
        paint(true);
        click(moved);
        emit(src || 'key', { rod: i });
    }

    /* ─────────── pointer: a tap toggles, a drag along the rod pushes ─────────── */
    let press = null;
    host.addEventListener('pointerdown', (e) => {
        if (st.locked) return;
        const b = e.target.closest('.ab-bead');
        if (!b || b.closest('.ab-quarter')) return;
        const r = b.closest('.ab-rod');
        press = { id: e.pointerId, x: e.clientX, y: e.clientY, b, i: +r.dataset.i, deck: b.dataset.deck, j: +b.dataset.j, done: false, type: e.pointerType };
    });
    host.addEventListener('pointermove', (e) => {
        if (!press || press.id !== e.pointerId || press.done) return;
        const five = KINDS[st.kind].five;
        // a finger on the soroban is the page's scroll (touch-action) — a drag is mouse and pen
        if (five && press.type === 'touch') return;
        const dx = e.clientX - press.x, dy = e.clientY - press.y;
        const along = five ? dy : dx, across = five ? dx : dy;
        if (Math.abs(along) < 7 || Math.abs(along) < Math.abs(across)) return;
        press.done = true;
        const rod = st.rods[press.i];
        const cur = press.deck === 'u' ? rod.u : rod.l;
        // toward the beam (or to the left on the schoty) counts the bead and those before it
        let toward;
        if (five) toward = press.deck === 'u' ? along > 0 : along < 0;
        else toward = along < 0;
        const next = toward ? Math.max(cur, press.j + 1) : Math.min(cur, press.j);
        setRodCount(press.i, press.deck, next, 'user');
        focusable(press.i);
    });
    const endPress = (e) => { if (press && press.id === e.pointerId) { press.dragged = press.done; setTimeout(() => { press = null; }, 0); } };
    host.addEventListener('pointerup', endPress);
    host.addEventListener('pointercancel', (e) => { if (press && press.id === e.pointerId) press = null; });
    host.addEventListener('click', (e) => {
        if (st.locked) return;
        const b = e.target.closest('.ab-bead');
        if (!b || b.closest('.ab-quarter')) return;
        // the click of the same press that already dragged
        if (press && press.done && e.detail !== 0) return;
        const r = b.closest('.ab-rod');
        const i = +r.dataset.i, deck = b.dataset.deck, j = +b.dataset.j;
        const rod = st.rods[i];
        const cur = deck === 'u' ? rod.u : rod.l;
        setRodCount(i, deck, j < cur ? j : j + 1, 'user');
        focusable(i);
    });

    /* ─────────── keyboard on the focused rod ─────────── */
    function focusable(i) {
        st.focusIdx = i;
        rodEls.forEach((r, k) => { r.tabIndex = k === i ? 0 : -1; });
    }
    host.addEventListener('focusin', (e) => {
        const r = e.target.closest && e.target.closest('.ab-rod');
        if (r && r.dataset.i != null) focusable(+r.dataset.i);
    });
    host.addEventListener('keydown', (e) => {
        const r = e.target.closest && e.target.closest('.ab-rod');
        if (!r || r.dataset.i == null || e.ctrlKey || e.metaKey || e.altKey) return;
        const i = +r.dataset.i;
        const five = KINDS[st.kind].five;
        const v = rodValue(st.kind, st.rods[i]);
        const max = five ? 9 : 10;
        const go = (k) => { const t = Math.max(0, Math.min(st.n - 1, k)); focusable(t); rodEls[t].focus(); };
        const k = e.key;
        const letter = window.__keyLetter ? window.__keyLetter(e) : k;
        let handled = true;
        if (five ? k === 'ArrowLeft' : k === 'ArrowUp') go(i - 1);
        else if (five ? k === 'ArrowRight' : k === 'ArrowDown') go(i + 1);
        else if (five ? k === 'ArrowUp' : k === 'ArrowLeft') { if (!st.locked) setRodDigit(i, Math.min(max, v + 1)); }
        else if (five ? k === 'ArrowDown' : k === 'ArrowRight') { if (!st.locked) setRodDigit(i, Math.max(0, v - 1)); }
        else if (k === 'Home') go(0);
        else if (k === 'End') go(st.n - 1);
        else if (/^[0-9]$/.test(letter)) { if (!st.locked) { setRodDigit(i, +letter); go(i + 1); } }
        else if (k === 'Backspace' || k === 'Delete') { if (!st.locked) setRodDigit(i, 0); if (k === 'Backspace') go(i - 1); }
        else handled = false;
        if (handled) e.preventDefault();
    });

    /* ─────────── hints: the beads of the next move glow ─────────── */
    function clearHints() {
        if (!st.hints.length) return;
        st.hints.forEach((b) => b.classList.remove('hint', 'hint-in', 'hint-out'));
        st.hints = [];
        rodEls.forEach((r) => r.classList.remove('hint'));
    }
    /** Mark the beads a step will move: in — they come to the beam, out — they leave it. */
    function hint(step) {
        clearHints();
        if (!step) return;
        const r = rodEls[step.rod];
        if (!r) return;
        const rod = st.rods[step.rod];
        r.classList.add('hint');
        const mark = (deck, from, to) => {
            if (from === to) return;
            const lo = Math.min(from, to), hi = Math.max(from, to);
            r.querySelectorAll(`.ab-bead[data-deck="${deck}"]`).forEach((b) => {
                const j = +b.dataset.j;
                if (j >= lo && j < hi) { b.classList.add('hint', to > from ? 'hint-in' : 'hint-out'); st.hints.push(b); }
            });
        };
        mark('u', rod.u, rod.u + step.du);
        mark('l', rod.l, rod.l + step.dl);
    }

    /* ─────────── API ─────────── */
    const api = {
        setKind(kind, n, o = {}) {
            st.kind = kind;
            st.n = n;
            st.dec = o.dec || 0;
            st.kopecks = !!o.kopecks && !KINDS[kind].five;
            if (st.kopecks) st.dec = 2;
            const keep = o.keepValue != null ? digitsOf(o.keepValue, n) : null;
            st.rods = keep ? rodsOf(kind, keep) : Array.from({ length: n }, () => ({ u: 0, l: 0 }));
            st.focusIdx = -1;
            build();
        },
        fit(W, H) {
            st.g = geometryFor(Math.max(120, W), Math.max(90, H));
            layout();
            paint(false);
            const g = st.g;
            return { w: g.w + (g.five ? 0 : g.digW), h: g.h + (g.five ? g.digH : 0) };
        },
        /** Size per bead unit for given box, without applying (for the layout's decisions). */
        measure(W, H) { const g = geometryFor(W, H); return { w: g.w + (g.five ? 0 : g.digW), h: g.h + (g.five ? g.digH : 0), bead: g.five ? g.bh : g.bw }; },
        setRods(rods, o = {}) {
            st.rods = rods.map((r) => ({ u: r.u, l: r.l }));
            clearHints();
            paint(o.animate !== false, !!o.slow);
            if (o.emit) emit(o.emit);
        },
        setScaled(scaled, o = {}) {
            const d = digitsOf(scaled, st.n);
            if (!d) return false;
            api.setRods(rodsOf(st.kind, d), o);
            if (o.animate !== false) click(3);
            return true;
        },
        clear(o = {}) { api.setRods(st.rods.map(() => ({ u: 0, l: 0 })), o); if (o.sound !== false) click(4); },
        applyStep(s, o = {}) {
            const rod = st.rods[s.rod];
            if (!rod) return;
            rod.u += s.du; rod.l += s.dl;
            clearHints();
            paint(true, !!o.slow);
            click(Math.abs(s.du) + Math.abs(s.dl));
            if (o.emit !== false) emit('step', { rod: s.rod });
        },
        hint,
        clearHints,
        rods: () => st.rods.map((r) => ({ u: r.u, l: r.l })),
        scaled: () => scaledOf(st.kind, st.rods),
        get kind() { return st.kind; },
        get n() { return st.n; },
        get dec() { return st.dec; },
        get kopecks() { return st.kopecks; },
        setLocked(v) { st.locked = !!v; host.classList.toggle('locked', st.locked); },
        setDigits(v) { st.digitsOn = !!v; if (st.g) { st.g = null; } },
        setVeil(v, text) { veil.hidden = !v; veil.textContent = text || ''; },
        focus() { const r = rodEls[st.focusIdx >= 0 ? st.focusIdx : st.n - 1]; if (r) r.focus({ preventScroll: true }); },
        rodEl: (i) => rodEls[i],
        placeName,
    };
    return api;
}

/* ─────────── a picture of an abacus for paper (flash cards, «draw the number») ─────────── */

/** An SVG of the soroban / schoty showing `digits` (null — all beads away: a blank to draw on). */
export function abacusSvg(kind, n, digits, o = {}) {
    const K = KINDS[kind];
    const ink = o.ink || '#1f2937', soft = o.soft || '#9ca3af', fill = o.fill || '#ffffff';
    const rods = digits ? rodsOf(kind, digits) : Array.from({ length: n }, () => ({ u: 0, l: 0 }));
    const parts = [];
    if (K.five) {
        const bh = 10, bw = 17, P = 20, fs = 6, ft = 5, upH = (K.up + 1) * bh + 1, lowH = (K.low + 1) * bh + 1, beamH = 4;
        const W = 2 * fs + n * P, H = 2 * ft + upH + beamH + lowH;
        parts.push(`<rect x="1" y="1" width="${W - 2}" height="${H - 2}" rx="4" fill="none" stroke="${ink}" stroke-width="2"/>`);
        parts.push(`<rect x="${fs}" y="${ft + upH}" width="${W - 2 * fs}" height="${beamH}" fill="${ink}"/>`);
        for (let i = 0; i < n; i++) {
            const cx = fs + i * P + P / 2;
            parts.push(`<line x1="${cx}" y1="${ft}" x2="${cx}" y2="${H - ft}" stroke="${soft}" stroke-width="1"/>`);
            const unit = n - 1;
            if ((unit - i) % 3 === 0) parts.push(`<circle cx="${cx}" cy="${ft + upH + beamH / 2}" r="1.3" fill="${fill}"/>`);
            const r = rods[i];
            for (let j = 0; j < K.up; j++) {
                const y = j < r.u ? ft + upH - (j + 1) * bh : ft + (K.up - 1 - j) * bh;
                parts.push(bicone(cx, y + bh / 2, bw, bh - 1, j < r.u, ink, fill));
            }
            for (let j = 0; j < K.low; j++) {
                const y = j < r.l ? ft + upH + beamH + j * bh : ft + upH + beamH + lowH - (K.low - j) * bh;
                parts.push(bicone(cx, y + bh / 2, bw, bh - 1, j < r.l, ink, fill));
            }
        }
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${o.width || W * 2}">${parts.join('')}</svg>`;
    }
    const bw = 8, bh = 14, rowP = 16, fs = 6, ft = 5, len = 14 * bw;
    const W = 2 * fs + len, H = 2 * ft + n * rowP;
    parts.push(`<rect x="1" y="1" width="${W - 2}" height="${H - 2}" rx="4" fill="none" stroke="${ink}" stroke-width="2"/>`);
    for (let i = 0; i < n; i++) {
        const cy = ft + i * rowP + rowP / 2;
        parts.push(`<line x1="${fs}" y1="${cy}" x2="${W - fs}" y2="${cy}" stroke="${soft}" stroke-width="1"/>`);
        const r = rods[i];
        for (let j = 0; j < 10; j++) {
            const x = j < r.l ? fs + j * bw : fs + len - (10 - j) * bw;
            const dark = j === 4 || j === 5;
            parts.push(`<ellipse cx="${x + bw / 2}" cy="${cy}" rx="${bw / 2 - 0.4}" ry="${bh / 2 - 0.5}" fill="${dark ? ink : (j < r.l ? soft : fill)}" stroke="${ink}" stroke-width="1"/>`);
        }
    }
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${o.width || W * 2}">${parts.join('')}</svg>`;
}
function bicone(cx, cy, w, h, on, ink, fill) {
    const x0 = cx - w / 2, x1 = cx + w / 2, a = w * 0.26;
    const pts = `${x0},${cy} ${x0 + a},${cy - h / 2} ${x1 - a},${cy - h / 2} ${x1},${cy} ${x1 - a},${cy + h / 2} ${x0 + a},${cy + h / 2}`;
    return `<polygon points="${pts}" fill="${on ? ink : fill}" stroke="${ink}" stroke-width="1"/>`;
}
